import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';

import { ContractCreateModal } from './ContractCreateModal.jsx';
import { ContractDetailModal } from './ContractDetailModal.jsx';

export function ContractsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['inventory', 'contracts'], queryFn: () => inventoryApi.listContracts().then((r) => r.items) });
  const rows = list.data || [];

  const columns = [
    { key: 'number', label: t('ct.col.number'), render: (r) => <span className="mono">{r.contractNumber}</span> },
    { key: 'supplier', label: t('ct.col.supplier'), render: (r) => r.supplierName },
    { key: 'product', label: t('ct.col.product'), render: (r) => <span><strong>{r.productSku}</strong> <span className="muted text-sm">{r.productName}</span></span> },
    { key: 'target', label: t('ct.col.target'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.targetQuantity)}</span> },
    { key: 'released', label: t('ct.col.released'), align: 'right', render: (r) => <span className="mono num">{formatNumber(r.releasedQuantity)}</span> },
    { key: 'remaining', label: t('ct.col.remaining'), align: 'right', render: (r) => <span className="mono num"><strong>{formatNumber(r.remainingQuantity)}</strong></span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={r.status === 'ACTIVE' ? 'success' : 'neutral'}>{t(`ct.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('ct.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('ct.title')}</h1>
          <p className="muted">{t('ct.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('ct.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('ct.metric.active')} value={String(rows.filter((r) => r.status === 'ACTIVE').length)} trendVariant="success" />
        <MetricCard label={t('ct.metric.total')} value={String(rows.length)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('ct.empty')} />

      <ContractCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ContractDetailModal contractId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
